import { useState, useEffect, useCallback } from "react";
import Editor from "@monaco-editor/react";
import type { SectionArtifact, SectionBackup } from "../../client";
import {
  getSectionCode,
  saveSectionCode,
  saveAndRenderSection,
  getSectionBackups,
  restoreSectionBackup,
  validateSectionCode,
  getSectionVideoUrl,
  regenerateSection,
} from "../../client";

type SectionEditorProps = {
  section: SectionArtifact;
  directory: string;
  onRenderComplete?: () => void;
};

export function SectionEditor({ section, directory, onRenderComplete }: SectionEditorProps) {
  const [code, setCode] = useState<string>("");
  const [originalCode, setOriginalCode] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [backups, setBackups] = useState<SectionBackup[]>([]);
  const [showBackups, setShowBackups] = useState(false);
  const [renderOutput, setRenderOutput] = useState<string | null>(null);
  const [showOutput, setShowOutput] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [promptOverride, setPromptOverride] = useState("");

  const hasChanges = code !== originalCode;

  // Load code on section change
  useEffect(() => {
    setLoading(true);
    setError(null);
    setValidationError(null);
    setRenderOutput(null);

    getSectionCode(directory, section.section_id)
      .then((loadedCode) => {
        setCode(loadedCode);
        setOriginalCode(loadedCode);
      })
      .catch((err) => {
        setError(err.message || "Failed to load code");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [directory, section.section_id]);

  // Debounced validation
  useEffect(() => {
    if (!code || code === originalCode) {
      setValidationError(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const result = await validateSectionCode(section.section_id, code);
        setValidationError(result.valid ? null : result.error || "Invalid syntax");
      } catch {
        // Ignore validation errors during typing
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [code, originalCode, section.section_id]);

  const loadBackups = useCallback(async () => {
    try {
      const data = await getSectionBackups(directory, section.section_id);
      setBackups(data);
    } catch {
      // Ignore backup loading errors
    }
  }, [directory, section.section_id]);

  const handleSave = async () => {
    if (!hasChanges || validationError) return;

    setSaving(true);
    setError(null);
    try {
      const result = await saveSectionCode(directory, section.section_id, code);
      setOriginalCode(code);
      setLastSaved(result.saved_at || new Date().toISOString());
      loadBackups();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndRender = async () => {
    if (validationError) return;

    setRendering(true);
    setError(null);
    setRenderOutput(null);
    try {
      const result = await saveAndRenderSection(directory, section.section_id, code);

      if (result.save.success) {
        setOriginalCode(code);
        setLastSaved(result.save.saved_at || new Date().toISOString());
      }

      if (result.render.success) {
        setRenderOutput(`Render successful!\nVideo: ${result.render.video_filename || "output.mp4"}`);
        onRenderComplete?.();
      } else {
        const output = [
          "Render failed!",
          result.render.error || "",
          "",
          "STDOUT:",
          result.render.stdout || "(empty)",
          "",
          "STDERR:",
          result.render.stderr || "(empty)",
        ].join("\n");
        setRenderOutput(output);
      }
      setShowOutput(true);
      loadBackups();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to render");
    } finally {
      setRendering(false);
    }
  };

  const handleRestore = async (backup: SectionBackup) => {
    try {
      await restoreSectionBackup(directory, section.section_id, backup.filename);
      // Reload code
      const newCode = await getSectionCode(directory, section.section_id);
      setCode(newCode);
      setOriginalCode(newCode);
      setShowBackups(false);
      loadBackups();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to restore backup");
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    setError(null);
    try {
      const result = await regenerateSection(directory, section.section_id, promptOverride || undefined);
      if (!result.success) {
        throw new Error(result.error || "Regeneration failed");
      }
      const newCode = result.code || "";
      setCode(newCode);
      setOriginalCode(newCode);
      setValidationError(null);
      setLastSaved(new Date().toISOString());
      loadBackups();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to regenerate code");
    } finally {
      setRegenerating(false);
    }
  };

  const handleRevert = () => {
    setCode(originalCode);
    setValidationError(null);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#888" }}>
        Loading code...
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", color: "#e0e0e0" }}>
      {/* Header */}
      <div
        style={{
          padding: "0.75rem",
          borderBottom: "1px solid #333",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "0.5rem",
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 600 }}>{section.title}</h3>
          <div style={{ fontSize: "0.75rem", color: "#888" }}>
            Section {section.section_id} • {section.code_filename}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          {hasChanges && (
            <button
              onClick={handleRevert}
              style={{
                padding: "0.375rem 0.75rem",
                backgroundColor: "transparent",
                border: "1px solid #666",
                borderRadius: "4px",
                color: "#888",
                fontSize: "0.75rem",
                cursor: "pointer",
              }}
            >
              Revert
            </button>
          )}

          <button
            onClick={() => {
              loadBackups();
              setShowBackups(!showBackups);
            }}
            style={{
              padding: "0.375rem 0.75rem",
              backgroundColor: "transparent",
              border: "1px solid #666",
              borderRadius: "4px",
              color: "#888",
              fontSize: "0.75rem",
              cursor: "pointer",
            }}
          >
            Backups
          </button>

          <button
            onClick={handleSave}
            disabled={!hasChanges || saving || !!validationError}
            style={{
              padding: "0.375rem 0.75rem",
              backgroundColor: hasChanges && !validationError ? "#1976d2" : "#333",
              border: "none",
              borderRadius: "4px",
              color: hasChanges && !validationError ? "#fff" : "#666",
              fontSize: "0.75rem",
              cursor: hasChanges && !validationError ? "pointer" : "not-allowed",
            }}
          >
            {saving ? "Saving..." : "Save"}
          </button>

          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            style={{
              padding: "0.375rem 0.75rem",
              backgroundColor: "#8e6ff9",
              border: "none",
              borderRadius: "4px",
              color: "#fff",
              fontSize: "0.75rem",
              cursor: regenerating ? "wait" : "pointer",
              opacity: regenerating ? 0.7 : 1,
            }}
          >
            {regenerating ? "Regenerating…" : "Regenerate (LLM)"}
          </button>

          <button
            onClick={handleSaveAndRender}
            disabled={rendering || !!validationError}
            style={{
              padding: "0.375rem 0.75rem",
              backgroundColor: !validationError ? "#2e7d32" : "#333",
              border: "none",
              borderRadius: "4px",
              color: !validationError ? "#fff" : "#666",
              fontSize: "0.75rem",
              cursor: !validationError ? "pointer" : "not-allowed",
            }}
          >
            {rendering ? "Rendering..." : "Save & Render"}
          </button>
        </div>
      </div>

      {/* Prompt override for regeneration */}
      <div
        style={{
          padding: "0.5rem 0.75rem",
          borderBottom: "1px solid #333",
          backgroundColor: "#101010",
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem", fontSize: "0.75rem", color: "#c0c0c0" }}>
          <span>Prompt hint (optional, used for regeneration):</span>
          <textarea
            value={promptOverride}
            onChange={(e) => setPromptOverride(e.target.value)}
            placeholder="Add a short hint or constraints to guide regeneration"
            rows={2}
            style={{
              width: "100%",
              backgroundColor: "#181818",
              color: "#e0e0e0",
              border: "1px solid #333",
              borderRadius: "4px",
              padding: "0.5rem",
              fontSize: "0.8rem",
            }}
          />
        </label>
      </div>

      {/* Status bar */}
      <div
        style={{
          padding: "0.375rem 0.75rem",
          backgroundColor: "#1a1a1a",
          borderBottom: "1px solid #333",
          display: "flex",
          gap: "1rem",
          fontSize: "0.7rem",
          color: "#888",
        }}
      >
        {hasChanges && <span style={{ color: "#ffa726" }}>Unsaved changes</span>}
        {validationError && <span style={{ color: "#ef5350" }}>{validationError}</span>}
        {lastSaved && !hasChanges && <span style={{ color: "#66bb6a" }}>Saved at {new Date(lastSaved).toLocaleTimeString()}</span>}
        {error && <span style={{ color: "#ef5350" }}>{error}</span>}
      </div>

      {/* Backups dropdown */}
      {showBackups && (
        <div
          style={{
            padding: "0.75rem",
            backgroundColor: "#1a1a1a",
            borderBottom: "1px solid #333",
            maxHeight: "200px",
            overflow: "auto",
          }}
        >
          <div style={{ fontSize: "0.75rem", fontWeight: 600, marginBottom: "0.5rem" }}>Backups</div>
          {backups.length === 0 ? (
            <div style={{ fontSize: "0.75rem", color: "#888" }}>No backups available</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              {backups.map((backup) => (
                <div
                  key={backup.filename}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0.375rem",
                    backgroundColor: "#252525",
                    borderRadius: "4px",
                  }}
                >
                  <span style={{ fontSize: "0.7rem" }}>{backup.timestamp}</span>
                  <button
                    onClick={() => handleRestore(backup)}
                    style={{
                      padding: "0.25rem 0.5rem",
                      backgroundColor: "#e65100",
                      border: "none",
                      borderRadius: "3px",
                      color: "#fff",
                      fontSize: "0.65rem",
                      cursor: "pointer",
                    }}
                  >
                    Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Editor */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <Editor
          height="100%"
          language="python"
          theme="vs-dark"
          value={code}
          onChange={(value) => setCode(value || "")}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            lineNumbers: "on",
            scrollBeyondLastLine: false,
            wordWrap: "on",
            automaticLayout: true,
            tabSize: 4,
            insertSpaces: true,
          }}
        />
      </div>

      {/* Render output */}
      {showOutput && renderOutput && (
        <div
          style={{
            maxHeight: "200px",
            overflow: "auto",
            borderTop: "1px solid #333",
            backgroundColor: renderOutput.includes("failed") ? "#1a0000" : "#001a00",
          }}
        >
          <div
            style={{
              padding: "0.5rem 0.75rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #333",
            }}
          >
            <span style={{ fontSize: "0.75rem", fontWeight: 600 }}>Render Output</span>
            <button
              onClick={() => setShowOutput(false)}
              style={{
                padding: "0.25rem 0.5rem",
                backgroundColor: "transparent",
                border: "none",
                color: "#888",
                fontSize: "0.75rem",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
          <pre
            style={{
              margin: 0,
              padding: "0.75rem",
              fontSize: "0.7rem",
              whiteSpace: "pre-wrap",
              fontFamily: "monospace",
              color: renderOutput.includes("failed") ? "#ef9a9a" : "#a5d6a7",
            }}
          >
            {renderOutput}
          </pre>
        </div>
      )}

      {/* Video preview (if rendered) */}
      {section.status === "rendered" && (
        <div style={{ borderTop: "1px solid #333", padding: "0.5rem" }}>
          <video
            key={`${section.section_id}-${lastSaved}`}
            src={getSectionVideoUrl(directory, section.section_id)}
            controls
            style={{ width: "100%", maxHeight: "200px", backgroundColor: "#000" }}
          />
        </div>
      )}
    </div>
  );
}
